import React from 'react';
import { withTranslation } from 'react-i18next';
import {
    Container,
    FormGroup,
    Popover,
    PopoverBody,
    PopoverHeader,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner,
    Row,
    Col,

} from 'reactstrap';
import { LogLevel, HubConnectionBuilder } from '@microsoft/signalr';
import cls from 'classnames';
import {
    faPencilAlt,
    faCheck,
    faTruck,
    faToolbox,
    faCommentDots,
    faCheckCircle,
    faExclamationTriangle,
    faComments,
    faHardHat,
    faUser,
    faEyeLowVision,
    faAngleDoubleLeft,
    faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { crewLeadLabel } from '@s20/i18n';
import { Link } from '@s20/components/common/Link';
import { appendTimeZone, formatOffsetInTimeZone } from '@s20/common/util/date';
import { getGroupSmsUrl, getMapUrl } from '@s20/common/util/dom';
import { getCounty } from '@s20/common/util/location';
import { httpClient } from '@s20/services/httpClient';
import {
    UncontrolledCollapseUnderlineHeader,
    FlexColumnCenter,
    FlexBetweenRow,
    FlexColumnStart,
    FlexCenterRow,
    FlexEndRow,
    FlexStartRow,
    FormLabel,
    SmallButton,
    toasty,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes, AppNavPaths, TimesheetAttachmentScope } from '../Common';
import './TimesheetForm.scss';
import authService from '../api-authorization/AuthorizeService';
import { JobDetails } from '../job/Job';
import {
    ContractTimesheetResponsibilityType,
    TimesheetResponsibilityType,
} from '../tenant/Tenant';
import { CountyLabel } from '../uscounties/CountyLabel';
import FileList from '../common/forms/FileList';
import JotFormModal from '../jotForm/JotFormModal';
import { ConfirmationStatus } from '../dispatch/Dispatch';
import FileUpload from '../common/forms/FileUpload';
import LoadingOverlay from '../common/LoadingOverlay';
import { Modal as Prompt } from '../common/Modal';
// import { CallOffButton } from '../callOff/CallOffButton';
import { RejectionNote, ClientCancelled, TimesheetStatus } from './Timesheet';
import EmployeeReview from './EmployeeReview';
import ClientReview from './ClientReview';
import TimesheetItem from './TimesheetItem';

const onFileDownload = (file) => {
    if (file?.id) {
        const url = ApiRoutes.fileUpload.getAttachmentById(file.id);
        httpClient.open(url, '_blank');
    }
};

class TimesheetForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.timesheetItemRef = React.createRef();
        this.timesheetClientReviewRef = React.createRef();
        this.timesheetEmployeeReviewRef = React.createRef();

        this.state = {
            assignment: null,
            confirmedAssignments: [],
            invalidChargeTypeIds: [],
            loading: true,
            notFound: false,
            timesheetOpen: false,
            timesheetClientReviewOpen: false,
            timesheet: {},
            timesheetId: null,
            showLinkedTimesheetModal: false,
            isSavingLinkedTimesheet: false,
            enablePreJobForm: false,
            preJobFormSubmission: null,
            showJotFormModal: false,
            showPreJobFormWarning: false,
            selectedTimesheetId: null,
            usCounties: [],
            attachmentsLoading: false,
            showAttachmentWarning: false,
            allowTimesheetAttachments: false,
            skipAttachments: false,
            jotFormsHubConnection: null,
            preJobFormLoading: false,
        };
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount() {
        if (this._subscription) {
            authService.unsubscribe(this._subscription);
        }
        this.disconnectJotFormsSignalR();
    }

    onAddTimesheet = async () => {
        const { timesheet } = this.state;
        this.setState({ isSavingLinkedTimesheet: true });

        const parentTimesheet = timesheet.timesheets.find(
            (x) => x.linkedTimesheetId == null,
        );
        const result = await httpClient.post(
            ApiRoutes.timesheet.createLinkedTimesheet(parentTimesheet.id),
        );

        if (result) {
            toasty.success(this.props.t('timesheet:timesheetAdded'));
        } else {
            let error = this.props.t('timesheet:timesheetAddError');
            if ((result ?? {}).error) {
                error = result.error;
            }
            toasty.error(error);
        }

        this.setState({
            isSavingLinkedTimesheet: false,
            showLinkedTimesheetModal: false,
        });
        await this.refresh();
    };

    onTimesheetClientReviewClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    onTimesheetEmployeeReviewClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    onTimesheetItemClosed = async () => {
        await this.context.setFormOpened(false);

        await this.refresh();
    };

    async openTimesheet(id) {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetItemRef.current.open(id);
        }
    }

    async openTimesheetClientReview(id) {
        // This should only open up once all billable charges on TS have time or NA.
        // It can also be opened up to view once its been filled out to review if needed(basically same as above).

        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetClientReviewRef.current.open(id);
        }
    }

    validateForm(id) {
        const { tenantSettings } = { ...this.context.tenant };

        const {
            preJobFormSubmission,
            allowTimesheetAttachments,
            timesheet,
            attachmentsLoading,
            skipAttachments,
        } = { ...this.state };

        const enablePreJobForm = Boolean(
            tenantSettings && tenantSettings.enablePreJobForm && timesheet?.usePreJobForm,
        );

        const noAttachments = !Array.isArray(timesheet?.attachments)
            || timesheet.attachments.length === 0;

        if (allowTimesheetAttachments && !skipAttachments && (noAttachments || attachmentsLoading)) {
            this.setState({ selectedTimesheetId: id });
            this.toggleAttachmentWarningModal();
            return;
        }

        const preJobFormComplete = preJobFormSubmission?.status?.id === 2 || preJobFormSubmission?.status?.id === 3;
        if (!enablePreJobForm || preJobFormComplete) {
            this.openTimesheetEmployeeReview(id);
        } else {
            this.setState({ selectedTimesheetId: id });
            this.togglePreJobFormWarningModal(id);
        }
    }

    async openTimesheetEmployeeReview(id) {
        if (id) {
            await this.context.setFormOpened(true);
            this.timesheetEmployeeReviewRef.current.open(id);
        }
    }

    populateState = async () => {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const timesheetId = this.props.match.params.id;
            const assignmentId = this.props.match.params.assignmentId || 0;

            if (!timesheetId) {
                this.setState({ notFound: true, loading: false });
                return;
            }

            // Timesheet
            const timesheet = await httpClient.js(
                ApiRoutes.timesheet.overview(timesheetId),
            );

            if (!timesheet) {
                this.setState({ notFound: true, loading: false });
                return;
            }

            const usCounties = await httpClient.js(ApiRoutes.USCounties.all());

            timesheet.clientReviewPopoverOpen = false;
            timesheet.crewReviewPopoverOpen = false;

            // Assignment
            let enforceCrewLeadTS = false;
            let assignment = null;

            if (assignmentId > 0) {
                assignment = await httpClient.js(
                    ApiRoutes.jobAssignment.byId(assignmentId),
                );
                if (assignment) {
                    enforceCrewLeadTS = this.context.tenant.tenantSettings
                        .timesheetResponsibilityType
                        === TimesheetResponsibilityType.CrewLead
                        || (this.context.tenant.tenantSettings
                            .timesheetResponsibilityType
                            === TimesheetResponsibilityType.Contract
                            && assignment.contractTimesheetResponsibilityTypeId
                            === ContractTimesheetResponsibilityType.CrewLead);
                }
            }

            const { tenantSettings } = { ...this.context.tenant };

            const enablePreJobForm = Boolean(
                tenantSettings
                && tenantSettings.enablePreJobForm
                && timesheet?.usePreJobForm,
            );

            const preJobFormSubmission = enablePreJobForm ?
                await httpClient.js(
                    ApiRoutes.jotForms.getPreJobFormSubmission(timesheetId),
                ) : null;

            const allowTimesheetAttachments = Boolean(
                tenantSettings
                && tenantSettings.allowTimesheetAttachments,
            ) || timesheet?.allowFileAttachments;

            this.setState({
                timesheet,
                assignment,
                timesheetId,
                loading: false,
                enforceCrewLeadTimesheet: enforceCrewLeadTS,
                enablePreJobForm,
                preJobFormSubmission,
                usCounties,
                allowTimesheetAttachments,
            },
                () => {
                    if (enablePreJobForm) {
                        this.setupJotFormsSignalR(timesheetId);
                    } else {
                        this.disconnectJotFormsSignalR();
                    }
                });
        }
    };

    setupJotFormsSignalR = (timesheetId) => {
        if (!timesheetId || this.state.jotFormsHubConnection) {
            return;
        }

        const hubConnection = new HubConnectionBuilder()
            .withUrl('jotFormsHub')
            .configureLogging(LogLevel.Error)
            .withAutomaticReconnect()
            .build();

        const groupName = `Timesheet-${timesheetId}`;

        hubConnection.on('PreJobFormSubmissionUpdated', (json) => {
            try {
                if (!this.state.enablePreJobForm) {
                    return;
                }

                const data = JSON.parse(json);
                const submissionId = data?.submissionId;
                const preJobFormSubmission = data?.preJobFormSubmission;
                const currentSubmissionId = this.state.preJobFormSubmission?.id;

                if (!preJobFormSubmission || !currentSubmissionId) {
                    return;
                }

                if (submissionId === currentSubmissionId) {
                    this.setState({ preJobFormSubmission });
                }
            } catch {
                // Ignore bad payloads
            }
        });

        hubConnection.onreconnected(() => {
            hubConnection.invoke('JoinGroup', groupName).catch();
        });

        hubConnection
            .start()
            .then(() => {
                hubConnection.invoke('JoinGroup', groupName).catch();
                this.setState({ jotFormsHubConnection: hubConnection });
            })
            .catch();
    };

    disconnectJotFormsSignalR = () => {
        const { jotFormsHubConnection, timesheetId } = this.state;
        if (jotFormsHubConnection) {
            const groupName = `Timesheet-${timesheetId}`;
            jotFormsHubConnection.invoke('LeaveGroup', groupName).catch(() => { });
            jotFormsHubConnection.stop().catch(() => { });
            this.setState({ jotFormsHubConnection: null });
        }
    };

    refresh = async () => {
        const { timesheetId } = this.state;
        const timesheet = await httpClient.js(
            ApiRoutes.timesheet.overview(timesheetId),
        );

        if (!timesheet) this.setState({ notFound: true, loading: false });

        // create review items for the signature grids

        this.setState({
            timesheet,
            loading: false,
        });
    };

    toggleLinkedTimesheetModal = () => {
        const { t } = this.props;
        Prompt.show({
            title: t('timesheet:addTimesheet'),
            onSubmit: () => this.onAddTimesheet(),
            SaveButtonProps: {
                label: t('common:add'),
                icon: faPlus,
            },
            body: () => (
                <p className="text-danger">
                    {t('timesheet:linkedTimesheetNote')}
                </p>
            ),
        });
    };

    togglePreJobFormWarningModal = () => {
        const { showPreJobFormWarning } = this.state;
        this.setState({ showPreJobFormWarning: !showPreJobFormWarning });
    };

    toggleAttachmentWarningModal = () => {
        Prompt.show({
            title: this.props.t('timesheet:fileAttachmentsRequired'),
            onSubmit: () => this.continueWithoutAttachment(),
            SaveButtonProps: {
                color: 'danger',
                label: this.props.t('timesheet:yesContinue'),
            },
            body: () => this.props.t('timesheet:attachmentsWarning'),
        });
    };

    crateNewPreJobForm = async () => {
        this.setState({ preJobFormLoading: true });

        const { timesheetId } = this.state;

        const preJobFormSubmission =
            await httpClient.js(
                ApiRoutes.jotForms.newPreJobFormSubmission(timesheetId),
            );
        this.setState({ preJobFormSubmission, showJotFormModal: true, preJobFormLoading: false });
    };

    toggleJotFormModal = () => {
        const { showJotFormModal } = this.state;
        this.setState({ showJotFormModal: !showJotFormModal });
    };

    continueWithoutPreJobForm = () => {
        this.setState({ showPreJobFormWarning: false });
        const { selectedTimesheetId } = this.state;
        this.openTimesheetEmployeeReview(selectedTimesheetId);
    };

    continueWithoutAttachment = () => {
        const { selectedTimesheetId } = this.state;
        this.setState({ showAttachmentWarning: false, skipAttachments: true }, () => this.validateForm(selectedTimesheetId));
    };

    continuePreJobFormSubmittion = async () => {
        const { preJobFormSubmission } = this.state;
        this.setState({ showPreJobFormWarning: false, preJobFormLoading: true });

        if (!preJobFormSubmission) {
            await this.crateNewPreJobForm();
        } else {
            this.toggleJotFormModal();
        }
        this.setState({ showPreJobFormWarning: false, preJobFormLoading: false });
    };

    onUploadComplete = async (uploads) => {
        this.setState({ attachmentsLoading: true });
        if (uploads?.length) {
            const { timesheetId } = this.state;
            try {
                const attachmentIds = uploads.map((upload) => upload.id);
                await httpClient.put(
                    ApiRoutes.timesheet.updateAttachments(timesheetId, TimesheetAttachmentScope.Documents),
                    attachmentIds);
                toasty.success(this.props.t('timesheet:attachmentsUpdated'));
            } catch (error) {
                toasty.error(this.props.t('timesheet:attachmentsUpdateFailed'));
                console.error('Error updating attachments:', error);
            }
        }
        await this.setAttachments();
        this.setState({ attachmentsLoading: false });
    };

    onFileDelete = async (attachment) => {
        const { timesheetId } = this.state;
        try {
            const response = await httpClient.delete(
                ApiRoutes.timesheet.deleteAttachment(timesheetId, attachment.id, TimesheetAttachmentScope.Documents),
            );
            if (response) {
                await this.setAttachments();
                toasty.success(this.props.t('timesheet:attachmentDeleted'));
            }
        } catch (error) {
            toasty.error(this.props.t('timesheet:attachmentDeleteFailed'));
            console.error('Error deleting attachment:', error);
        }
    };

    setAttachments = async () => {
        const { timesheetId, timesheet } = this.state;
        const attachments = await httpClient.get(
            ApiRoutes.timesheet.getAttachments(timesheetId, TimesheetAttachmentScope.Documents),
        );
        this.setState({
            timesheet: {
                ...timesheet,
                attachments,
            },
        });
    };

    render() {
        const {
            timesheet,
            assignment,
            confirmedAssignments,
            loading,
            notFound,
            enablePreJobForm,
            preJobFormSubmission,
            showJotFormModal,
            showPreJobFormWarning,
            allowTimesheetAttachments,
            attachmentsLoading,
            preJobFormLoading,
        } = { ...this.state };

        const confirmed = (assignment ?? {}).confirmationStatusId
            === ConfirmationStatus.Confirmed;
        const crewLead = crewLeadLabel(this.context.tenant?.tenantSettings);

        const jobStartTime = appendTimeZone(timesheet.jobStartTime, timesheet.jobStartTimeOffset, timesheet.timeZone);

        if (loading || !(this.context.tenant ?? {}).tenantSettings) {
            return (
                <div className="confirmationContainer pt-2 d-flex flex-row align-items-center justify-content-center">
                    <Spinner />
                </div>
            );
        }
        const { tenantSettings } = { ...this.context.tenant };

        const allEquipment = assignment?.equipment ?? [];

        // What equipment does the confirming employee own of this list?
        const ownedEquipment = allEquipment.filter(
            (x) => x.equipmentEmployeeAssignmentId
                === assignment.assignedEmployeeId,
        );
        const ownedEquipmentIds = ownedEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        // Of the equipment, which have been assigned to any of the equipment owned by the confirming employee?
        const dependentEquipment = allEquipment.filter((x) => ownedEquipmentIds.includes(x.equipmentAssignedEquipmentId));
        const dependentEquipmentIds = dependentEquipment.map(
            (x) => x.assignedEquipmentId,
        );

        const showContractDescriptionInTimesheetView = Boolean(
            tenantSettings
            && tenantSettings.showContractDescriptionInTimesheetView,
        );

        const { t } = this.props;
        const jobDetailsHeader = showContractDescriptionInTimesheetView
            && timesheet
            && timesheet.contractDescription
            ? t('timesheet:jobDetailsWithName', { name: timesheet.contractDescription })
            : t('timesheet:jobDetails');

        const county = getCounty(this.state.usCounties, {
            usCountyId: timesheet.usCounty?.id,
            contractUnionWorkTypeId: timesheet.contractUnionWorkTypeId,
        });

        return (
            <>
                <div
                    className={cls('confirmationContainer pt-2', {
                        'site-form-disabled': !!this.context.formIsOpen,
                    })}
                >
                    <Container size="sm">
                        {!!notFound && (
                            <h4 className="text-center">
                                {t('confirm:notFound')}
                            </h4>
                        )}
                        {!!timesheet && (
                            <>
                                {(timesheet ?? {}).isClientCancelled && (
                                    <FlexColumnCenter className="pb-3">
                                        <ClientCancelled />
                                    </FlexColumnCenter>
                                )}
                                {(timesheet ?? {}).timesheetStatusId
                                    === TimesheetStatus.Rejected && (
                                        <FlexColumnCenter className="pb-3">
                                            <RejectionNote
                                                note={
                                                    timesheet.lastRejectionNote
                                                }
                                            />
                                        </FlexColumnCenter>
                                    )}
                                {(timesheet ?? {}).timesheetStatusId
                                    === TimesheetStatus.OperationsReview && (
                                        <FlexColumnCenter className="pb-3">
                                            <div
                                                className="m-0 mt-1 mb-1 site-alert site-alert-danger d-flex flex-column"
                                                role="alert"
                                            >
                                                <FlexStartRow>
                                                    <small>
                                                        <strong className="mr-1">
                                                            {t('timesheet:operationsReviewNotice')}
                                                        </strong>
                                                    </small>
                                                </FlexStartRow>
                                                <span className="mt-1" />
                                            </div>
                                        </FlexColumnCenter>
                                    )}
                                <UncontrolledCollapseUnderlineHeader
                                    headerText={jobDetailsHeader}
                                >
                                    <Link
                                        component={Button}
                                        size="sm"
                                        color="link"
                                        className="text-info text-right p-0 pb-1 m-0"
                                        to={AppNavPaths.AssignmentCalendar}
                                    >
                                        <FontAwesomeIcon
                                            icon={faAngleDoubleLeft}
                                            className="mr-1"
                                        />
                                        {t('timesheet:backToCalendar')}
                                    </Link>
                                    <JobDetails
                                        customerName={timesheet?.customerName}
                                        dispatchOfficeName={timesheet?.dispatchOfficeName}
                                        subcontractorName={timesheet?.subcontractorName}
                                        foremanName={timesheet?.foremanName}
                                        foremanPhone={timesheet?.foremanPhone}
                                        jobNotes={timesheet?.jobNotes}
                                        jobNumber={timesheet?.jobNumber}
                                        jobStartTime={jobStartTime}
                                        jobId={timesheet?.jobId}
                                        projectId={timesheet?.projectId}
                                        lastRejectionNote={timesheet?.lastRejectionNote}
                                        workOrderNumber={timesheet?.workOrderNumber}
                                        isEmergency={timesheet?.isEmergency}
                                        isTimesheetForm
                                        isTimesheet
                                        isPayroll={false}
                                        isBilling={false}
                                        tenantSettings={tenantSettings}
                                        jobName={timesheet?.contractDescription}
                                    />

                                    <div className="locationDetails">
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="meetingLocation"
                                                label={t('timesheet:meetingLocation')}
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                {Boolean(timesheet?.meetingLocationUrlFormatted) && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(timesheet.meetingLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                                <span
                                                    id="meetingLocation"
                                                    name="meetingLocation"
                                                >
                                                    {timesheet.meetingLocation ?? '-'}
                                                </span>
                                            </FlexStartRow>
                                        </FormGroup>
                                        <FormGroup>
                                            <FormLabel
                                                htmlFor="jobLocation"
                                                label={t('timesheet:jobLocation')}
                                                className="bottom-border"
                                            />
                                            <FlexStartRow>
                                                {Boolean(timesheet?.jobLocationUrlFormatted) && (
                                                    <a
                                                        rel="noopener noreferrer"
                                                        className="text-white mr-2 btn btn-sm btn-success p-1"
                                                        title={t('timesheet:openInGoogleMaps')}
                                                        href={getMapUrl(timesheet.jobLocationUrlFormatted)}
                                                        target="_blank"
                                                    >
                                                        <i className="fa fa-map-marked-alt" />
                                                    </a>
                                                )}
                                                <span
                                                    id="jobLocation"
                                                    name="jobLocation"
                                                >
                                                    {timesheet.jobLocation ?? '-'}
                                                </span>
                                            </FlexStartRow>
                                        </FormGroup>

                                        {Boolean(timesheet?.usCounty) && (
                                            <FormGroup>
                                                <FormLabel
                                                    htmlFor="usCounty"
                                                    label={t('timesheet:county')}
                                                    className="bottom-border"
                                                />
                                                <FlexStartRow>
                                                    <div>
                                                        <CountyLabel {...county} />
                                                    </div>
                                                </FlexStartRow>
                                            </FormGroup>
                                        )}
                                    </div>
                                </UncontrolledCollapseUnderlineHeader>

                                {!!tenantSettings.jobUploadsEnabled
                                    && !!(timesheet.jobUploads ?? [])
                                        .length && (
                                        <div className="mt-3">
                                            <FormGroup>
                                                <FormLabel
                                                    label={t('timesheet:timesheetAttachments')}
                                                    className="bottom-border"
                                                />
                                                <FileList
                                                    jobId={timesheet.jobId}
                                                    files={timesheet.jobUploads ?? []}
                                                />
                                            </FormGroup>

                                        </div>
                                    )}

                                {assignment != null
                                    && assignment.confirmationStatusId
                                    !== 5 && (
                                        <div className="crewAndEquipmentDetails">
                                            <FormGroup>
                                                <FormLabel
                                                    label={t('confirm:crewAndEquipment')}
                                                    className="bottom-border"
                                                />
                                            </FormGroup>
                                            {!!confirmed
                                                && !!assignment.crewSMS && (
                                                    <FlexCenterRow>
                                                        <a
                                                            className="mb-2 p-1 btn btn-success btn-sm groupSmsLink text-white"
                                                            href={getGroupSmsUrl(assignment.crewSMS.split(','))}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faComments
                                                                }
                                                                size="lg"
                                                                className="mr-2"
                                                            />
                                                            {t('confirm:sendGroupSmsToCrew')}
                                                        </a>
                                                    </FlexCenterRow>
                                                )}

                                            <div className="crewAndEquipmentDetails table-responsive">
                                                <table className="table table-sm assignmentsTable">
                                                    <tbody>
                                                        {!!assignment.crew && (
                                                            <>
                                                                {assignment.crew.map(
                                                                    (c) => {
                                                                        const ownedAssignment = c.assignedEmployeeId
                                                                            === assignment.assignedEmployeeId;

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        owned: !!ownedAssignment,
                                                                                    },
                                                                                )}
                                                                                key={
                                                                                    c.id
                                                                                }
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                                'fa-hard-hat-golden':
                                                                                                    !!c.isCrewLead,
                                                                                            },
                                                                                        )}
                                                                                        icon={
                                                                                            c.isCrewLead
                                                                                                ? faHardHat
                                                                                                : faUser
                                                                                        }
                                                                                        title={
                                                                                            c.isCrewLead
                                                                                                ? crewLead
                                                                                                : c.assignedEmployeeGroup
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <FlexBetweenRow>
                                                                                        <small
                                                                                            className={cls(
                                                                                                {
                                                                                                    'text-success font-weight-bold':
                                                                                                        c.assignedEmployeeId
                                                                                                        === assignment.assignedEmployeeId,
                                                                                                },
                                                                                            )}
                                                                                        >
                                                                                            {c.isCrewLead
                                                                                                ? t('confirm:assignedEmployeeWithLead', { name: c.assignedEmployeeName, lead: crewLead })
                                                                                                : c.assignedEmployeeName}
                                                                                            {' '}
                                                                                            {parseInt(assignment.dispatchOfficeId) !== c.employeeDispatchCompany.id
                                                                                                ? t('confirm:dispatchedFrom', { name: c.employeeDispatchCompany.name })
                                                                                                : ''}
                                                                                        </small>
                                                                                        {!!confirmed
                                                                                            && !!c.assignedEmployeePhone && (
                                                                                                <a
                                                                                                    title={t('timesheet:sendSmsTo', { name: c.assignedEmployeeName })}
                                                                                                    className="ml-2 p-1 btn btn-sm btn-success"
                                                                                                    href={`sms://+1${c.assignedEmployeePhone}`}
                                                                                                >
                                                                                                    <FontAwesomeIcon
                                                                                                        icon={
                                                                                                            faCommentDots
                                                                                                        }
                                                                                                        size="lg"
                                                                                                    />
                                                                                                </a>
                                                                                            )}
                                                                                    </FlexBetweenRow>
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed
                                                                                        && c.confirmationStatusId
                                                                                        === ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.5,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title={t('timesheet:employeeConfirmed')}
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    },
                                                                )}
                                                            </>
                                                        )}
                                                        {!!assignment.equipment && (
                                                            <>
                                                                {assignment.equipment.map(
                                                                    (e) => {
                                                                        const icon = e.assignedEquipmentIsVehicle ? faTruck : faToolbox;

                                                                        const ownedAssignment = e.equipmentEmployeeAssignmentId
                                                                            === assignment.assignedEmployeeId
                                                                            || dependentEquipmentIds.includes(
                                                                                e.assignedEquipmentId,
                                                                            );

                                                                        return (
                                                                            <tr
                                                                                className={cls(
                                                                                    'assignmentRow',
                                                                                    {
                                                                                        owned: !!ownedAssignment,
                                                                                    },
                                                                                )}
                                                                                key={
                                                                                    e.id
                                                                                }
                                                                            >
                                                                                <td className="iconCell">
                                                                                    <FontAwesomeIcon
                                                                                        className={cls(
                                                                                            {
                                                                                                'text-success font-weight-bold':
                                                                                                    !!ownedAssignment,
                                                                                                'text-muted':
                                                                                                    !ownedAssignment,
                                                                                            },
                                                                                        )}
                                                                                        icon={
                                                                                            icon
                                                                                        }
                                                                                    />
                                                                                </td>
                                                                                <td>
                                                                                    <FlexColumnStart>
                                                                                        <small
                                                                                            className={cls({
                                                                                                'text-success font-weight-bold': !!ownedAssignment,
                                                                                            })}
                                                                                        >
                                                                                            {t('confirm:assignedEquipmentBase', { type: e.assignedEquipmentType, name: e.assignedEquipmentName })}
                                                                                            {e.quantity > 1 && ` ${t('confirm:quantitySuffix', { quantity: e.quantity })}`}
                                                                                            {parseInt(assignment.dispatchOfficeId) !== e.equipmentDispatchLocation.id
                                                                                                ? ` ${t('confirm:dispatchedFrom', { name: e.equipmentDispatchLocation.name })}`
                                                                                                : ''}
                                                                                        </small>

                                                                                        {e.equipmentEmployeeAssignmentName && (
                                                                                            <small>{t('confirm:assignedTo', { name: e.equipmentEmployeeAssignmentName })}</small>
                                                                                        )}

                                                                                        {e.equipmentAssignedEquipmentName && (
                                                                                            <small>{t('confirm:assignedToEquipment', { type: e.equipmentAssignedEquipmentType, name: e.equipmentAssignedEquipmentName })}</small>
                                                                                        )}

                                                                                        {Boolean(e.isRentalAssignment) && (
                                                                                            <>
                                                                                                {e.assignedEquipmentIsGeneric ? (
                                                                                                    <>
                                                                                                        {e.rentalDropoffQuantity > 0 && (
                                                                                                            <div>
                                                                                                                <small>
                                                                                                                    {t('confirm:dropoffWithQty', { quantity: e.rentalDropoffQuantity })}
                                                                                                                </small>
                                                                                                            </div>
                                                                                                        )}

                                                                                                        {e.rentalPickupQuantity > 0 && (
                                                                                                            <div>
                                                                                                                <small>
                                                                                                                    {t('confirm:pickupWithQty', { quantity: e.rentalPickupQuantity })}
                                                                                                                </small>
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <div>
                                                                                                        <small>
                                                                                                            {e.isRentalPickup ? t('confirm:pickup') : t('confirm:dropoff')}
                                                                                                        </small>
                                                                                                    </div>
                                                                                                )}
                                                                                            </>
                                                                                        )}
                                                                                    </FlexColumnStart>
                                                                                </td>
                                                                                <td className="text-right">
                                                                                    {!confirmed
                                                                                        && e.confirmationStatusId
                                                                                        === ConfirmationStatus.Confirmed && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.7,
                                                                                                }}
                                                                                                size="lg"
                                                                                                title={t('timesheet:equipmentConfirmed')}
                                                                                                className="icon-check checked"
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                    {!confirmed
                                                                                        && e.confirmationStatusId
                                                                                        !== ConfirmationStatus.Confirmed
                                                                                        && ownedAssignment && (
                                                                                            <FontAwesomeIcon
                                                                                                style={{
                                                                                                    opacity:
                                                                                                        ownedAssignment
                                                                                                            ? 1
                                                                                                            : 0.7,
                                                                                                }}
                                                                                                size="lg"
                                                                                                className={cls(
                                                                                                    'cursor-pointer icon-check',
                                                                                                    {
                                                                                                        checked:
                                                                                                            confirmedAssignments.indexOf(
                                                                                                                e.id,
                                                                                                            )
                                                                                                            > -1,
                                                                                                    },
                                                                                                )}
                                                                                                title={
                                                                                                    confirmedAssignments.indexOf(
                                                                                                        e.id,
                                                                                                    )
                                                                                                        > -1
                                                                                                        ? t('timesheet:confirmedTooltip')
                                                                                                        : t('timesheet:clickToConfirmTooltip')
                                                                                                }
                                                                                                icon={
                                                                                                    faCheckCircle
                                                                                                }
                                                                                            />
                                                                                        )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    },
                                                                )}
                                                            </>
                                                        )}
                                                        {!!assignment.setupCharges && (
                                                            <>
                                                                {assignment.setupCharges.map(
                                                                    (e) => (
                                                                        <tr
                                                                            className="assignmentRow owned"
                                                                            key={
                                                                                e
                                                                            }
                                                                        >
                                                                            <td className="iconCell">
                                                                                <FontAwesomeIcon
                                                                                    className="text-success font-weight-bold"
                                                                                    icon={
                                                                                        faToolbox
                                                                                    }
                                                                                />
                                                                            </td>
                                                                            <td>
                                                                                <FlexColumnStart>
                                                                                    <small
                                                                                        className="text-success font-weight-bold"
                                                                                    >
                                                                                        {String(e)}
                                                                                    </small>
                                                                                </FlexColumnStart>
                                                                            </td>
                                                                            <td className="text-right"></td>
                                                                        </tr>
                                                                    ),
                                                                )}
                                                            </>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                {enablePreJobForm && (
                                    <>
                                        <FormGroup
                                            className="bottom-border"
                                        >
                                            <FormLabel
                                                htmlFor="preJobForm"
                                                label={t('timesheet:submitPreJobForm')}
                                                className="bottom-border"
                                            />
                                            {!preJobFormSubmission && (
                                                <Row>
                                                    <Col>
                                                        <SmallButton
                                                            type="button"
                                                            className="ml-1"
                                                            title={t('timesheet:submitNewPreJobTooltip')}
                                                            disabled={preJobFormLoading}
                                                            onClick={
                                                                this.crateNewPreJobForm
                                                            }
                                                        >
                                                            {preJobFormLoading && (
                                                                <Spinner
                                                                    size="sm"
                                                                    className="mr-1"
                                                                />
                                                            )}

                                                            {!preJobFormLoading && <i className="fa fa-plus-circle fa-lg mr-1" />}
                                                            {t('timesheet:submitPreJobForm')}
                                                        </SmallButton>
                                                    </Col>
                                                    <Col
                                                        style={{ textAlign: 'right', marginRight: '12px', color: 'red' }}
                                                    >
                                                        <i className="fa fa-lg fa-times-circle mr-1" />
                                                        <span
                                                            style={{ fontWeight: 'bold' }}
                                                        >
                                                            {t('timesheet:preJobNotSubmitted')}
                                                        </span>
                                                    </Col>
                                                </Row>
                                            )}
                                            {preJobFormSubmission && (
                                                <Row>
                                                    <Col>
                                                        <SmallButton
                                                            type="button"
                                                            className="ml-1"
                                                            title={t('timesheet:viewEditPreJobTooltip')}
                                                            onClick={
                                                                this.toggleJotFormModal
                                                            }
                                                        >
                                                            <i className="fa fa-eye fa-lg mr-1" />
                                                            {t('timesheet:viewEditPreJobForm')}
                                                        </SmallButton>
                                                    </Col>
                                                    <Col style={{ textAlign: 'right', marginRight: '12px' }}>
                                                        <i className="fa fa-check mr-1" />
                                                        <span>{preJobFormSubmission?.status?.description ?? ''}</span>
                                                    </Col>
                                                </Row>
                                            )}
                                        </FormGroup>
                                        <JotFormModal
                                            isOpen={showJotFormModal}
                                            toggle={this.toggleJotFormModal}
                                            formSubmission={this.state.preJobFormSubmission}
                                            onSubmissionCompleted={(submission) => this.setState({ preJobFormSubmission: submission })}
                                        />
                                    </>
                                )}

                                {allowTimesheetAttachments && (
                                    <FormGroup className="mt-3">
                                        <FormLabel
                                            htmlFor="timesheetAttachments"
                                            label={t('timesheet:timesheetAttachments')}
                                            className="bottom-border"
                                        />
                                        <LoadingOverlay show={attachmentsLoading}>
                                            <FileUpload
                                                url={ApiRoutes.fileUpload.createAttachment()}
                                                accept={['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.doc', '.docx', '.xls', '.xlsx']}
                                                files={timesheet.attachments || []}
                                                onQueueComplete={this.onUploadComplete}
                                                onDownload={onFileDownload}
                                                onDelete={this.onFileDelete}
                                                type="simple"
                                                buttonText={t('timesheet:uploadFiles')}
                                                disabled={attachmentsLoading}// || timesheet.timesheetStatusId === TimesheetStatus.Submitted}
                                            />
                                        </LoadingOverlay>
                                    </FormGroup>
                                )}

                                {timesheet.allowLinkedWorkOrders && (
                                    <FlexEndRow>
                                        <SmallButton
                                            type="button"
                                            className="ml-1"
                                            title=""
                                            onClick={
                                                this
                                                    .toggleLinkedTimesheetModal
                                            }
                                        >
                                            <i className="fa fa-plus-circle fa-lg mr-1" />
                                            {t('timesheet:addTimesheet')}
                                        </SmallButton>
                                    </FlexEndRow>
                                )}
                                <div className="table-responsive">
                                    <table
                                        id="timesheetsTable"
                                        className="table table-sm"
                                    >
                                        <thead>
                                            <tr>
                                                <th className="border-bottom-0">{t('timesheet:timesheetHeader')}</th>
                                                {/* <th className="text-center"> */}
                                                {/*    <FlexColumnCenter> */}
                                                {/*        <span>Action</span> */}
                                                {/*    </FlexColumnCenter> */}
                                                {/* </th> */}
                                                <th className="client border-bottom-0">
                                                    <FlexColumnCenter>
                                                        <span>{t('timesheet:clientSig')}</span>
                                                    </FlexColumnCenter>
                                                </th>
                                                <th className="crew border-bottom-0">
                                                    <FlexColumnCenter>
                                                        <span>{t('timesheet:crewSig')}</span>
                                                    </FlexColumnCenter>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(timesheet.timesheets ?? []).length ? (
                                                <tr>
                                                    <td
                                                        colSpan="4"
                                                        className="text-center"
                                                    >
                                                        {t('timesheet:noTimesheetsFound')}
                                                    </td>
                                                </tr>
                                            ) : (
                                                timesheet.timesheets.map(
                                                    (timesheet, i) => {
                                                        const done = !!timesheet.clientReviewComplete
                                                            && !!timesheet.employeeReviewComplete;
                                                        const isDeleted = timesheet.statusId === TimesheetStatus.Deleted;

                                                        return (
                                                            <tr
                                                                key={
                                                                    timesheet.id
                                                                }
                                                                className={`timesheet-row ${isDeleted ? 'timesheet-deleted bg-light' : ''}`}
                                                            >
                                                                <td className="d-flex flex-row align-items-start">
                                                                    {timesheet.isComplete ? (
                                                                        <>
                                                                            <FontAwesomeIcon
                                                                                id={`timesheetStatusLink_${timesheet.id}`}
                                                                                icon={
                                                                                    faCheckCircle
                                                                                }
                                                                                className="mt-1 cursor-pointer mr-1 text-success"
                                                                            />
                                                                            <Popover
                                                                                trigger="focus"
                                                                                placement="right"
                                                                                isOpen={
                                                                                    timesheet.timesheetPopoverOpen
                                                                                }
                                                                                target={`timesheetStatusLink_${timesheet.id}`}
                                                                                toggle={() => this.setState(
                                                                                    (
                                                                                        state,
                                                                                    ) => (
                                                                                        (state.timesheet.timesheets[
                                                                                            i
                                                                                        ].timesheetPopoverOpen = !state
                                                                                            .timesheet
                                                                                            .timesheets[
                                                                                            i
                                                                                        ]
                                                                                            .timesheetPopoverOpen),
                                                                                        state
                                                                                    ),
                                                                                )}
                                                                            >
                                                                                <PopoverHeader className="success">
                                                                                    <FontAwesomeIcon
                                                                                        icon={
                                                                                            faCheckCircle
                                                                                        }
                                                                                        className="mr-1 text-success"
                                                                                    />
                                                                                    {t('timesheet:timesheetSubmittedHeader')}
                                                                                </PopoverHeader>
                                                                                <PopoverBody>
                                                                                    {t('timesheet:timesheetSubmittedBody')}
                                                                                </PopoverBody>
                                                                            </Popover>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon
                                                                                id={`timesheetStatusLink_${timesheet.id}`}
                                                                                icon={isDeleted ? faEyeLowVision : faExclamationTriangle}
                                                                                className={`mt-1 cursor-pointer mr-1 ${isDeleted ? 'text-muted' : 'text-danger'}`}
                                                                            />
                                                                            <Popover
                                                                                trigger="focus"
                                                                                placement="right"
                                                                                isOpen={
                                                                                    timesheet.timesheetPopoverOpen
                                                                                }
                                                                                target={`timesheetStatusLink_${timesheet.id}`}
                                                                                toggle={() => this.setState(
                                                                                    (
                                                                                        state,
                                                                                    ) => (
                                                                                        (state.timesheet.timesheets[
                                                                                            i
                                                                                        ].timesheetPopoverOpen = !state
                                                                                            .timesheet
                                                                                            .timesheets[
                                                                                            i
                                                                                        ]
                                                                                            .timesheetPopoverOpen),
                                                                                        state
                                                                                    ),
                                                                                )}
                                                                            >
                                                                                <PopoverHeader className="danger">
                                                                                    <FontAwesomeIcon
                                                                                        icon={
                                                                                            faExclamationTriangle
                                                                                        }
                                                                                        className="mr-1 text-danger"
                                                                                    />
                                                                                    {t('timesheet:timesheetIncompleteHeader')}
                                                                                </PopoverHeader>
                                                                                <PopoverBody>
                                                                                    {t('timesheet:timesheetIncompleteBody')}
                                                                                </PopoverBody>
                                                                            </Popover>
                                                                        </>
                                                                    )}
                                                                    <FlexColumnCenter>
                                                                        <Button
                                                                            color="link"
                                                                            className={`site-link ${isDeleted ? 'text-muted' : ''}`}
                                                                            onClick={() => this.openTimesheet(
                                                                                timesheet.id,
                                                                            )}
                                                                        >
                                                                            {t('timesheet:timesheetTitleParts', {
                                                                                customer: timesheet.customerName,
                                                                                subcontractorPart: timesheet.subcontractorName ? t('timesheet:subcontractorPart', { name: timesheet.subcontractorName }) : '',
                                                                                jobNumber: timesheet.timesheetNumber || timesheet.jobNumber,
                                                                            })}
                                                                        </Button>
                                                                        {!!timesheet.workOrderNumber && (
                                                                            <small>
                                                                                <span className="font-weight-bold mr-1">
                                                                                    {t('timesheet:woNumberPrefix')}
                                                                                </span>

                                                                                {timesheet.workOrderNumber}
                                                                            </small>
                                                                        )}
                                                                        {isDeleted && (
                                                                            <span className="font-italic text-muted">
                                                                                {t('timesheet:timesheetDeletedNotice')}
                                                                            </span>
                                                                        )}
                                                                    </FlexColumnCenter>
                                                                </td>
                                                                {/* <td className="actions text-center"> */}
                                                                {/*    <span className="site-link cursor-pointer" */}
                                                                {/*        onClick={() => this.openTimesheet(timesheet.id)} */}
                                                                {/*    > */}
                                                                {/*        {timesheet.timesheetBillableDetailsComplete ? 'View' : 'Edit'} */}
                                                                {/*    </span> */}
                                                                {/* </td> */}
                                                                <td className="client">
                                                                    {(
                                                                        // Condition #1: RetainClientSignatureOnUpdate is ON and clientReview NOT complete
                                                                        (!timesheet.clientReviewComplete && tenantSettings.retainClientSignatureOnUpdate)
                                                                        // OR Condition #2: Timesheet is billable-details-complete but client review is NOT complete
                                                                        || (timesheet.timesheetBillableDetailsComplete && !timesheet.clientReviewComplete)
                                                                    ) ? (
                                                                        <Button
                                                                            color="link"
                                                                            id={`timesheetLink_${timesheet.id}`}
                                                                            className="site-link text-center"
                                                                            title={t('timesheet:clientReviewDetails')}
                                                                            onClick={() => this.openTimesheetClientReview(
                                                                                timesheet.id,
                                                                            )}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faPencilAlt
                                                                                }
                                                                                size="lg"
                                                                                className="mr-2"
                                                                            />
                                                                        </Button>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon
                                                                                id={`clientReviewIcon_${timesheet.id}`}
                                                                                className={
                                                                                    timesheet.clientReviewComplete
                                                                                        ? 'text-success cursor-pointer'
                                                                                        : `cursor-pointer ${isDeleted ? 'text-muted' : 'text-danger'}`
                                                                                }
                                                                                title={
                                                                                    timesheet.clientReviewComplete
                                                                                        ? 'Complete - Click to Review'
                                                                                        : 'Complete the timesheet in order to do the Client Review.'
                                                                                }
                                                                                icon={
                                                                                    timesheet.clientReviewComplete
                                                                                        ? faCheck
                                                                                        : faExclamationTriangle
                                                                                }
                                                                                onClick={() => (timesheet.clientReviewComplete
                                                                                    ? this.openTimesheetClientReview(
                                                                                        timesheet.id,
                                                                                    )
                                                                                    : undefined)}
                                                                                size="lg"
                                                                                container="body"
                                                                            />
                                                                            {!timesheet.clientReviewComplete && (
                                                                                <Popover
                                                                                    trigger="focus"
                                                                                    placement="left"
                                                                                    isOpen={
                                                                                        timesheet.clientPopoverOpen
                                                                                    }
                                                                                    target={`clientReviewIcon_${timesheet.id}`}
                                                                                    toggle={() => this.setState(
                                                                                        (
                                                                                            state,
                                                                                        ) => (
                                                                                            (state.timesheet.timesheets[
                                                                                                i
                                                                                            ].clientPopoverOpen = !state
                                                                                                .timesheet
                                                                                                .timesheets[
                                                                                                i
                                                                                            ]
                                                                                                .clientPopoverOpen),
                                                                                            state
                                                                                        ),
                                                                                    )}
                                                                                >
                                                                                    <PopoverHeader className="danger">
                                                                                        <FontAwesomeIcon
                                                                                            icon={
                                                                                                faExclamationTriangle
                                                                                            }
                                                                                            className="mr-1 text-danger"
                                                                                        />
                                                                                        {t('timesheet:previousStepsIncompleteHeader')}
                                                                                    </PopoverHeader>
                                                                                    <PopoverBody>
                                                                                        {t('timesheet:previousStepsIncompleteBody')}
                                                                                    </PopoverBody>
                                                                                </Popover>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </td>
                                                                <td className="crew">
                                                                    {timesheet.timesheetDetailsComplete && !timesheet.employeeReviewComplete ? (
                                                                        <Button
                                                                            color="link"
                                                                            className="site-link text-center"
                                                                            title={t('timesheet:employeeReviewDetails')}
                                                                            onClick={() => this.validateForm(
                                                                                timesheet.id,
                                                                            )}
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={
                                                                                    faPencilAlt
                                                                                }
                                                                                size="lg"
                                                                                className="mr-2"
                                                                            />
                                                                        </Button>
                                                                    ) : (
                                                                        <>
                                                                            <FontAwesomeIcon
                                                                                id={`empReviewIcon_${timesheet.id}`}
                                                                                className={
                                                                                    timesheet.employeeReviewComplete
                                                                                        ? 'text-success'
                                                                                        : `cursor-pointer ${isDeleted ? 'text-muted' : 'text-danger'}`
                                                                                }
                                                                                title={
                                                                                    timesheet.employeeReviewComplete
                                                                                        ? t('timesheet:reviewCompleteTooltip')
                                                                                        : t('timesheet:reviewIncompleteTooltip')
                                                                                }
                                                                                icon={
                                                                                    timesheet.employeeReviewComplete
                                                                                        ? faCheck
                                                                                        : faExclamationTriangle
                                                                                }
                                                                                size="lg"
                                                                                container="body"
                                                                            />
                                                                            {!timesheet.employeeReviewComplete && (
                                                                                <Popover
                                                                                    trigger="focus"
                                                                                    placement="left"
                                                                                    isOpen={
                                                                                        timesheet.employeePopoverOpen
                                                                                    }
                                                                                    target={`empReviewIcon_${timesheet.id}`}
                                                                                    toggle={() => this.setState(
                                                                                        (
                                                                                            state,
                                                                                        ) => (
                                                                                            (state.timesheet.timesheets[
                                                                                                i
                                                                                            ].employeePopoverOpen = !state
                                                                                                .timesheet
                                                                                                .timesheets[
                                                                                                i
                                                                                            ]
                                                                                                .employeePopoverOpen),
                                                                                            state
                                                                                        ),
                                                                                    )}
                                                                                >
                                                                                    <PopoverHeader
                                                                                        className={
                                                                                            done
                                                                                                ? 'success'
                                                                                                : 'danger'
                                                                                        }
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={
                                                                                                done
                                                                                                    ? faCheck
                                                                                                    : faExclamationTriangle
                                                                                            }
                                                                                            className={`mr-1 ${done
                                                                                                ? 'text-success'
                                                                                                : 'text-danger'
                                                                                                }`}
                                                                                        />
                                                                                        {done
                                                                                            ? 'Complete'
                                                                                            : 'Previous Steps Incomplete'}
                                                                                    </PopoverHeader>
                                                                                    <PopoverBody>
                                                                                        {done
                                                                                            ? 'This timesheet is completed.'
                                                                                            : 'Ensure the timesheet is filled out and the client review is complete prior to employee review.'}
                                                                                    </PopoverBody>
                                                                                </Popover>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        );
                                                    },
                                                )
                                            )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                {!!(timesheet.rejectionHistory ?? []).length && (
                                    <UncontrolledCollapseUnderlineHeader
                                        headerText={t('timesheet:rejectionHistoryHeader')}
                                    >
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered table-striped">
                                                <thead className="text-muted">
                                                    <tr>
                                                        <th>{t('timesheet:rejectionDate')}</th>
                                                        <th>{t('timesheet:rejectedBy')}</th>
                                                        <th>{t('timesheet:rejectionNotes')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {timesheet.rejectionHistory.map((r) => (
                                                        <tr key={`rejection-${r.id}`}>
                                                            <td>
                                                                {formatOffsetInTimeZone(
                                                                    r.rejectedOn,
                                                                    'MM/DD/YYYY, h:mm A',
                                                                    timesheet.timeZone,
                                                                )}
                                                            </td>
                                                            <td>{r.rejectedByName}</td>
                                                            <td>{r.notes}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </UncontrolledCollapseUnderlineHeader>
                                )}
                            </>
                        )}
                    </Container>

                    {
                        // Employee call off is switched off for now. Uncomment to show the Call Off button again.
                        // {Boolean(assignment?.canCallOff) && (
                        //     <FlexCenterRow className="userActions p-1">
                        //         <CallOffButton
                        //             jobAssignmentId={assignment.jobAssignmentId}
                        //             onSuccess={() => this.context.history.push(AppNavPaths.AssignmentCalendar)}
                        //         />
                        //     </FlexCenterRow>
                        // )}
                    }
                </div>
                <Modal
                    isOpen={
                        showPreJobFormWarning
                    }
                    toggle={
                        this
                            .togglePreJobFormWarningModal
                    }
                >
                    <ModalHeader
                        toggle={
                            this
                                .togglePreJobFormWarningModal
                        }
                    >
                        {t('common:warning')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <p className="text-danger">
                                {t('timesheet:preJobIncompletePrompt')}
                            </p>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="danger"
                            onClick={
                                this.continueWithoutPreJobForm
                            }
                        >
                            {t('timesheet:yesContinue')}
                        </Button>
                        {' '}
                        <Button
                            color="success"
                            disabled={preJobFormLoading}
                            onClick={
                                this
                                    .continuePreJobFormSubmittion
                            }
                        >
                            {preJobFormLoading && (
                                <Spinner
                                    size="sm"
                                    className="mr-1"
                                />
                            )}

                            {t('timesheet:submitPreJobForm')}
                        </Button>
                        <Button
                            onClick={
                                this
                                    .togglePreJobFormWarningModal
                            }
                        >
                            {t('common:cancel')}
                        </Button>
                    </ModalFooter>
                </Modal>

                <TimesheetItem
                    ref={this.timesheetItemRef}
                    show={this.state.timesheetOpen}
                    toggleShow={(open) => this.setState({ timesheetOpen: open })}
                    onClose={this.onTimesheetItemClosed}
                />
                <ClientReview
                    tenantSettings={tenantSettings}
                    ref={this.timesheetClientReviewRef}
                    show={this.state.timesheetClientReviewOpen}
                    toggleShow={(open) => this.setState({ timesheetClientReviewOpen: open })}
                    onClose={this.onTimesheetClientReviewClosed}
                />
                <EmployeeReview
                    tenantSettings={tenantSettings}
                    ref={this.timesheetEmployeeReviewRef}
                    show={this.state.timesheetEmployeeReviewOpen}
                    toggleShow={(open) => this.setState({ timesheetEmployeeReviewOpen: open })}
                    onClose={this.onTimesheetEmployeeReviewClosed}
                />
            </>
        );
    }
}

export default withTranslation()(TimesheetForm);
